import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RouteFocus } from "@/components/RouteFocus";
import { Analytics } from "@/components/Analytics";
import { Seo, ORGANIZATION_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";
import { focusMainContent } from "@/lib/focusMain";
import Index from "./pages/Index.tsx";
import Blog from "./pages/Blog.tsx";
import BlogPost from "./pages/BlogPost.tsx";
import NotFound from "./pages/NotFound.tsx";
import WcagAuditPage from "./pages/WcagAuditPage.tsx";
import WcagAuditThankYou from "./pages/WcagAuditThankYou.tsx";
import Privacy from "./pages/Privacy.tsx";
import { ConsentBanner } from "@/components/ConsentBanner";

const queryClient = new QueryClient();

/**
 * App contents WITHOUT the router or HelmetProvider — those are supplied by the
 * entry point (BrowserRouter on the client in `main.tsx`; StaticRouter +
 * HelmetProvider context on the server in `entry-server.tsx`) so the same tree
 * can be both hydrated in the browser and pre-rendered to static HTML at build.
 */
export const AppInner = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      {/* Skip link — the first focusable element on every page. Lets keyboard
          users bypass the nav and jump straight to the main landmark (WCAG
          2.4.1 Bypass Blocks). Visually hidden until focused. */}
      <a
        href="#main-content"
        onClick={(e) => {
          // Focus the page heading (visible ring, below the fixed header)
          // instead of the full-width <main>. The href stays as the no-JS
          // fallback. (WCAG 2.4.7 — NCS-255)
          if (focusMainContent()) e.preventDefault();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg focus:outline-[3px] focus:outline-offset-2 focus:outline-focus-ring"
      >
        Skip to main content
      </a>
      <Toaster />
      {/* Sitewide defaults + Organization/WebSite structured data (AEO). Per-page
          <Seo> in each route overrides title/description/canonical. */}
      <Seo jsonLd={[ORGANIZATION_JSONLD, WEBSITE_JSONLD]} />
      <RouteFocus />
      {/* Fires an Umami pageview on client-side route changes (SSG-safe; no-op
          until VITE_UMAMI_WEBSITE_ID is configured). */}
      <Analytics />
      {/* Routed page content. Wrapped so the accessibility menu's visual filters
          (grayscale/invert) affect the page but never the menu itself. */}
      <div className="a11y-canvas">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Google Ads landing page + its post-submit page (own minimal layout). */}
          <Route path="/wcag-audit" element={<WcagAuditPage />} />
          <Route path="/wcag-audit/thank-you" element={<WcagAuditThankYou />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      {/* Google-tags cookie consent (Consent Mode v2); renders nothing unless
          GTM is configured. Outside .a11y-canvas so display filters skip it. */}
      <ConsentBanner />
    </TooltipProvider>
  </QueryClientProvider>
);

export default AppInner;
